import { useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { LiveTrainingCardProps } from 'src/components/LiveTrainingCard';
import { FILTER_SECTION_TITLES } from 'src/pages/MyLearningPage/children/hooks/constants';

const QUERY_PARAM_KEYS = {
  DATE: 'date',
  SEARCH: 'search',
  APPS: 'apps',
  FREE: 'free',
  ENROLLED: 'enrolled',
  SHOW: 'show', // For mobile filter menu
} as const;

interface UseCalendarFiltersReturn {
  // Filter values
  selectedDate: string | null;
  searchValue: string;
  selectedApps: string[];
  showFree: boolean;
  showEnrolled: boolean;

  // Handlers
  handleDateSelect: (value: { iso: string; day: number; month: number; year: number }) => void;
  handleSearch: (value: string) => void;
  handleAppFilterChange: (label: string, isCurrentlySelected: boolean) => void;
  handleFreeToggle: () => void;
  handleEnrolledToggle: () => void;
  clearSelectedDate: () => void;
  clearAllFilters: () => void;
  handleApplyFilters: (filters: Record<string, string[]>) => void;

  // Computed values
  allProducts: string[];
  appsItems: Record<string, { isSelected: boolean; isDisabled: boolean }>;
  hasActiveFilters: boolean;
  selectedFiltersLength: number;
  filterCounts: {
    apps: Record<string, number>;
    free: number;
    enrolled: number;
  };
  mobileFilterSections: Array<{
    key: string;
    title: string;
    options: Array<{ label: string; count: number }>;
  }>;
}

export const useCalendarFilters = (calendarEvents: LiveTrainingCardProps[]): UseCalendarFiltersReturn => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Extract filter values from URL params
  const selectedDate = searchParams.get(QUERY_PARAM_KEYS.DATE);
  const searchValue = searchParams.get(QUERY_PARAM_KEYS.SEARCH) || '';
  const selectedApps = useMemo(
    () => searchParams.get(QUERY_PARAM_KEYS.APPS)?.split(',').filter(Boolean) || [],
    [searchParams],
  );
  const showFree = searchParams.get(QUERY_PARAM_KEYS.FREE) === 'true';
  const showEnrolled = searchParams.get(QUERY_PARAM_KEYS.ENROLLED) === 'true';

  // Get unique products from all events
  const allProducts = useMemo(() => {
    const productsSet = new Set<string>();

    calendarEvents.forEach((event) => {
      event.products.forEach((product) => productsSet.add(product));
    });

    return Array.from(productsSet).sort();
  }, [calendarEvents]);

  // Create dropdown items for products
  const appsItems = useMemo(
    () =>
      allProducts.reduce(
        (acc, product) => ({
          ...acc,
          [product]: {
            isSelected: selectedApps.includes(product),
            isDisabled: false,
          },
        }),
        {} as Record<string, { isSelected: boolean; isDisabled: boolean }>,
      ),
    [allProducts, selectedApps],
  );

  // Calculate counts for each filter option
  const filterCounts = useMemo(() => {
    const appCounts: Record<string, number> = {};
    let freeCount = 0;
    let enrolledCount = 0;

    calendarEvents.forEach((event) => {
      // Count by product
      event.products.forEach((product) => {
        appCounts[product] = (appCounts[product] || 0) + 1;
      });

      // Count free and enrolled
      if (event.isFree) {
        freeCount++;
      }
      if (event.enrolled) {
        enrolledCount++;
      }
    });

    return {
      apps: appCounts,
      free: freeCount,
      enrolled: enrolledCount,
    };
  }, [calendarEvents]);

  // Mobile filter sections with counts
  const mobileFilterSections = useMemo(() => {
    return [
      {
        key: QUERY_PARAM_KEYS.APPS,
        title: FILTER_SECTION_TITLES.APP,
        options: allProducts.map((product) => ({
          label: product,
          count: filterCounts.apps[product] || 0,
        })),
      },
      {
        key: QUERY_PARAM_KEYS.SHOW,
        title: 'Show',
        options: [
          { label: 'Free', count: filterCounts.free || 0 },
          { label: 'Enrolled', count: filterCounts.enrolled || 0 },
        ],
      },
    ];
  }, [allProducts, filterCounts]);

  // Helper to keep show param in sync with free/enrolled params
  const updateShowParam = useCallback((params: URLSearchParams, isFree: boolean, isEnrolled: boolean) => {
    const showValues: string[] = [];

    if (isFree) {
      showValues.push('Free');
    }
    if (isEnrolled) {
      showValues.push('Enrolled');
    }

    if (showValues.length > 0) {
      params.set(QUERY_PARAM_KEYS.SHOW, showValues.join(','));
    } else {
      params.delete(QUERY_PARAM_KEYS.SHOW);
    }
  }, []);

  // Handlers
  const handleDateSelect = useCallback(
    (value: { iso: string; day: number; month: number; year: number }) => {
      const newParams = new URLSearchParams(searchParams);

      newParams.set(QUERY_PARAM_KEYS.DATE, value.iso);
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  const handleSearch = useCallback(
    (value: string) => {
      const newParams = new URLSearchParams(searchParams);

      if (value) {
        newParams.set(QUERY_PARAM_KEYS.SEARCH, value);
      } else {
        newParams.delete(QUERY_PARAM_KEYS.SEARCH);
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  const handleAppFilterChange = useCallback(
    (label: string, isCurrentlySelected: boolean) => {
      const newParams = new URLSearchParams(searchParams);
      const currentApps = selectedApps.filter((app) => app !== label);

      if (!isCurrentlySelected) {
        currentApps.push(label);
      }

      if (currentApps.length > 0) {
        newParams.set(QUERY_PARAM_KEYS.APPS, currentApps.join(','));
      } else {
        newParams.delete(QUERY_PARAM_KEYS.APPS);
      }

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams, selectedApps],
  );

  const handleFreeToggle = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    const newShowFree = !showFree;

    if (newShowFree) {
      newParams.set(QUERY_PARAM_KEYS.FREE, 'true');
    } else {
      newParams.delete(QUERY_PARAM_KEYS.FREE);
    }

    // Update show param for mobile filter menu
    updateShowParam(newParams, newShowFree, showEnrolled);
    setSearchParams(newParams);
  }, [searchParams, setSearchParams, showFree, showEnrolled, updateShowParam]);

  const handleEnrolledToggle = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    const newShowEnrolled = !showEnrolled;

    if (newShowEnrolled) {
      newParams.set(QUERY_PARAM_KEYS.ENROLLED, 'true');
    } else {
      newParams.delete(QUERY_PARAM_KEYS.ENROLLED);
    }

    // Update show param for mobile filter menu
    updateShowParam(newParams, showFree, newShowEnrolled);
    setSearchParams(newParams);
  }, [searchParams, setSearchParams, showFree, showEnrolled, updateShowParam]);

  const clearSelectedDate = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);

    newParams.delete(QUERY_PARAM_KEYS.DATE);
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const clearAllFilters = useCallback(() => {
    const newParams = new URLSearchParams();

    setSearchParams(newParams);
  }, [setSearchParams]);

  const handleApplyFilters = useCallback(
    (filters: Record<string, string[]>) => {
      const newParams = new URLSearchParams(searchParams);

      // Clear existing filter params
      newParams.delete(QUERY_PARAM_KEYS.APPS);
      newParams.delete(QUERY_PARAM_KEYS.FREE);
      newParams.delete(QUERY_PARAM_KEYS.ENROLLED);
      newParams.delete(QUERY_PARAM_KEYS.SHOW);

      // Apply new filters
      Object.entries(filters).forEach(([key, values]) => {
        if (key === QUERY_PARAM_KEYS.APPS && values.length > 0) {
          newParams.set(QUERY_PARAM_KEYS.APPS, values.join(','));
        }
        if (key === QUERY_PARAM_KEYS.SHOW) {
          const newShowFree = values.includes('Free');
          const newShowEnrolled = values.includes('Enrolled');

          if (newShowFree) {
            newParams.set(QUERY_PARAM_KEYS.FREE, 'true');
          }
          if (newShowEnrolled) {
            newParams.set(QUERY_PARAM_KEYS.ENROLLED, 'true');
          }

          // Also set the combined show param for mobile menu state persistence
          updateShowParam(newParams, newShowFree, newShowEnrolled);
        }
      });

      navigate({ search: newParams.toString() });
    },
    [searchParams, navigate, updateShowParam],
  );

  // Computed values
  const hasShowFilter = showFree || showEnrolled;
  const hasActiveFilters = Boolean(selectedDate || searchValue || selectedApps.length > 0 || hasShowFilter);
  const selectedFiltersLength = selectedApps.length + (searchValue ? 1 : 0) + (hasShowFilter ? 1 : 0);

  return {
    // Filter values
    selectedDate,
    searchValue,
    selectedApps,
    showFree,
    showEnrolled,

    // Handlers
    handleDateSelect,
    handleSearch,
    handleAppFilterChange,
    handleFreeToggle,
    handleEnrolledToggle,
    clearSelectedDate,
    clearAllFilters,
    handleApplyFilters,

    // Computed values
    allProducts,
    appsItems,
    hasActiveFilters,
    selectedFiltersLength,
    filterCounts,
    mobileFilterSections,
  };
};
